import { toast } from 'sonner';
import { processError, isSmartfloError, isNetworkError, isRetryable, logDiagnostics } from './error-handler';

export interface ShowErrorOptions {
  id?: string;
}

// Shows a contextual error toast based on error classification
export function showError(error: unknown, context?: string, options?: ShowErrorOptions): void {
  const appError = processError(error);
  logDiagnostics(appError);

  const message = context ? `${context}: ${appError.userMessage}` : appError.userMessage;
  const description = appError.errorId !== 'ERR-unknown' ? `Reference: ${appError.errorId}` : undefined;

  if (isSmartfloError(appError)) {
    toast.error(message, {
      id: options?.id,
      description,
      duration: 6000,
    });
    return;
  }

  if (isNetworkError(appError)) {
    toast.error(message, {
      id: options?.id,
      description,
      duration: 6000,
    });
    return;
  }

  toast.error(message, {
    id: options?.id,
    description,
    duration: appError.category === 'VALIDATION' ? 4000 : 5000,
  });
}

// Shows error toast with a retry action button
export function showErrorWithRetry(error: unknown, retryFn: () => void, context?: string, options?: ShowErrorOptions): void {
  const appError = processError(error);
  logDiagnostics(appError);

  if (!isRetryable(appError)) {
    showError(error, context, options);
    return;
  }

  const message = context ? `${context}: ${appError.userMessage}` : appError.userMessage;

  toast.error(message, {
    id: options?.id,
    description: appError.errorId !== 'ERR-unknown' ? `Reference: ${appError.errorId}` : undefined,
    duration: 8000,
    action: {
      label: 'Retry',
      onClick: retryFn,
    },
  });
}

// Handles auth errors by redirecting to login
export function handleAuthError(error: unknown): void {
  const appError = processError(error);
  if (appError.category === 'AUTH' && (appError.code === 'TOKEN_EXPIRED' || appError.code === 'TOKEN_INVALID' || appError.code === 'TOKEN_REQUIRED')) {
    toast.error(appError.userMessage);
    if (typeof window !== 'undefined') {
      window.location.href = `/login?callbackUrl=${encodeURIComponent(window.location.pathname)}`;
    }
  }
}
